<template>
  <div v-if="!isParentAccountEnabled">
    <h4 class="text-danger">Feature not available. Contact system administrator for further details.</h4>
  </div>
  <div class="page-container" v-else>
    <!-- User cards view -->
    <b-spinner class="m-5" v-if="loading"></b-spinner>
    <div v-if="!selectedUser" class="user-section">
      <div class="card-container">
        <UserCard v-for="user in users" :key="user.id" :user="user"
          @select-user="handleUserSelection" :onRemove="onRemove"  v-if="!loading" />
        <!-- Add Child Card -->
        <div class="user-card add-child-card" @click="showQrModal" v-if="!loading">
          <div class="card-image">
            <div class="add-icon-container">
              <i class="fas fa-user-plus add-icon"></i>
            </div>
          </div>
          <div class="card-content">
            <h4 class="card-title">Link Student Account</h4>
            <p class="card-text">Click here to link your child's student account.</p>
          </div>

        </div>
      </div>
    </div>
    <!-- Product cards view -->
    <div v-else class="product-section">
      <div class="header">
        <button class="back-button" @click="goBack">
          <i class="fas fa-arrow-left"></i> Back
        </button>

      </div>

      <!-- Status filter tabs -->
      <div class="status-tabs">
        <span class="status-tab" :class="{ active: activeStatus === 'active' }" @click="setActiveStatus('active')">
          ACTIVE <span class="count">{{ activeCount }}</span>
        </span>
        <span class="status-tab" :class="{ active: activeStatus === 'unenrolled' }"
          @click="setActiveStatus('unenrolled')">
          UNENROLLED/EXPIRED <span class="count">{{ unenrolledCount }}</span>
        </span>
        <span class="status-tab" :class="{ active: activeStatus === 'blocked' }" @click="setActiveStatus('blocked')">
          BLOCKED <span class="count">{{ blockedCount }}</span>
        </span>
      </div>

      <!-- Product display -->
      <div class="content-panel rounded">
        <div v-if="filteredVariants.length === 0" class="no-products text-center">
          <h4 class="title">{{ selectedUser.firstName }} {{ selectedUser.lastName }} has no courses in the selected
            category.</h4>
        </div>
        <div v-else class="card-container">
          <ProductCard v-for="variant in filteredVariants" :key="variant.productVariantID" :variant="variant"
            @navigate-to-overview="handleNavigateToOverview" />
        </div>
      </div>
    </div>
    <b-modal
      v-model="qrModal"
      centered
      no-close-on-esc
      overlay
      no-close-on-backdrop
      hide-header
      hide-footer
      content-class="qr-modal-content"
      size="xl"
    >
    <button
      type="button"
      class="btn-close position-absolute top-0 end-0 m-3"
      aria-label="Close"
      @click="closeQrModal"
    ></button>
      <div class="modal-body" style="height: 80vh; overflow: auto;">
        <!-- <div class="modal-header">
          <h4 class="modal-title">Parent QR Code</h4>
        </div> -->
        <div class="modal-content">
          <h3 class="mt-4">Scan the QR to Link Account</h3>
          <p>Link your account to your child’s student account to start tracking their progress.</p>

          <p><strong>How to link your account:</strong></p>

          <ol>

            <li>On your child’s device, log in to their student account.</li>
            <li>Go to the <strong>Profile</strong> page.</li>
            <li>Tap <strong>Associate Parent Account</strong>.</li>
            <li>Select <strong>Scan QR Code</strong>.</li>
            <li>
              When the camera opens, use your child’s device to scan the QR code shown on your screen.
              <p class="text-muted">Alternatively, if for any reason, you are not able to scan the QR code, you can enter the code <strong>{{user.id}}</strong> on your child's device.</p>
            </li>
            <li>
              Once linking process is done and success message appears on your child's device, just click DONE button on the bottom of this screen or refresh this page.
            </li>
            <li>
              Your child's account will now appear on your <a href="/parent/home">account's dashboard</a>.
            </li>
          </ol>

          <qrcode-vue class="qr-code mt-4" :value="user.id" :size="150" level="H" />
          <div class="text-center mt-4">
            <p class="text-muted fw-bold">OR</p>
          </div>
          <h4 class="text-center fw-bold">Enter code: {{user.id}}</h4>
          <div class="text-center mt-4 mb-4">
            <button
                class="btn btn-success"
                type="button"
                aria-label="DONE"
                @click="closeQrModal">
              DONE
            </button>
          </div>

        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions,mapState, mapGetters } from 'vuex';
import QrcodeVue from 'qrcode.vue';
import UserCard from '../../components/Parent/UserCard.vue';
import ProductCard from '../../components/Parent/ProductCard.vue';
import UserDashboardProducts from "../../components/UserDashboard/UserDashboardProducts.vue"

export default {
  name: 'ParentHome',
  components: { UserCard, ProductCard, UserDashboardProducts, QrcodeVue, },
  data() {
    return {
      loading: false,
      users: [],
      selectedUser: null,
      activeStatus: 'active',
      pageSize: 25,
      nextPageToken: null,
      searchString: null,
      qrModal: false,
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    ...mapGetters("appState", ["isParentAccountEnabled"]),
    activeCount() {
      return this.selectedUser?.productVariants?.filter(v => v.subscriptionStatus === 'Allowed').length || 0;
    },
    unenrolledCount() {
      return this.selectedUser?.productVariants?.filter(v => v.subscriptionStatus === 'Unenrolled').length || 0;
    },
    blockedCount() {
      return this.selectedUser?.productVariants?.filter(v => v.subscriptionStatus === 'Blocked').length || 0;
    },
    filteredVariants() {
      const filterMap = {
        active: v => v.subscriptionStatus === 'Allowed',
        unenrolled: v => v.subscriptionStatus === 'Unenrolled',
        blocked: v => v.subscriptionStatus === 'Blocked',
      };
      return this.selectedUser?.productVariants?.filter(filterMap[this.activeStatus]) || [];
    },
    isParentUser() {
      return this.user && this.user.type && this.user.type.toLowerCase() === 'parent';
    }
  },
  methods: {
    ...mapActions('user', ['getParentChildren','removeChildrenFromParent']),
    async fetchChildren() {
      if(this.loading) return;

      this.loading = true;
      try {
        const result = await this.getParentChildren();
        this.users = result;
        if (this.users.length > 0 && !this.selectedUser) {
          this.selectedUser = null; // Ensure initial view shows users, not auto-select
        }
      } catch (error) {
        // console.error('Failed to fetch users:', error);
        this.$bvToast.toast('Failed to load users', { title: 'Error', variant: 'danger', solid: true });
      }
      finally {
        this.loading = false;
      }
    },
    onRemove(userId){
      this.fetchChildren();
      // this.users = this.users.filter(user => user.id != userId);
    },
    handleUserSelection(user) {
      this.selectedUser = user;
      this.activeStatus = 'active';
    },
    setActiveStatus(status) {
      this.activeStatus = status;
    },
    goBack() {
      this.selectedUser = null;
    },
    handleNavigateToOverview(productVariantId) {
      if (this.selectedUser && productVariantId) {
        this.$router.push({
          path: `/parent/student/${this.selectedUser.id}/productvariant/${productVariantId}/overview`,
        });
      }
    },
    showQrModal() {
      this.qrModal = true;
    },
    closeQrModal() {
      this.qrModal = false;
      this.fetchChildren();
    },
  },
  created() {
    if(!this.isParentUser) {
      this.$router.push({ path: `/` });
    } else {
      this.fetchChildren();
    }
  },
  // computed:{
  //   ...mapState("productVariant", ["productVariantsProgress"]),
  //   progress() {
	// 		if (this.productVariantsProgress && this.productVariantsProgress[this.data.id]) {
	// 			var progressOB = this.productVariantsProgress[this.data.id];
	// 			return Math.round((progressOB.completedTotalContent / progressOB.totalContent) * 100);
	// 		}
	// 		return 0;
		// },
  // }
};
</script>

<style scoped>
.page-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  /* padding-right: 2.5rem; */
  min-height: calc(100vh - 3rem);
}

.spinner-border {
	border-color: var(--primary-color);
	border-right-color: transparent;

}

.user-section, .product-section {
  width: 100%;
  max-width: 1200px;
}

.section-title {
  margin: auto;
  font-weight: 600;
  color: var(--primary-color);
  margin-bottom: 1.5rem;
  text-align: center;
}

.card-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.status-tabs {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  justify-content: start;
}

.status-tab {
  padding-bottom: 0.5rem;
  cursor: pointer;
  color: #6c757d;
  font-weight: 500;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.status-tab.active {
  color: #000;
  border-bottom: 2px solid var(--primary-color);
  border-radius: 0;
}

.count {
  background-color: rgba(108, 117, 125);
  padding: 0.1rem 0.5rem;
  border-radius: .25rem;
  margin-left: 0.5rem;
  color: #fff;
}

.content-panel {
  padding: 1.5rem;
  border-radius: 10px;
}


.no-products {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6c757d;
}

.title {
  margin: 0;
}

.back-button {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 1rem;
}

.back-button i {
  margin-right: 0.5rem;
}

.header {
  display: flex;
  align-items: center;
  /* justify-content: space-between; */
  margin-top: 1rem;
  margin-bottom: 1.5rem;
}

.modal-header{
  border-bottom: none !important;
}

.qr-code{
  text-align: center;
}

.add-child-card {
  flex: 0 0 30%;
  margin-top: 1rem;
  border: 2px dashed #ddd;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: box-shadow 0.3s, background-color 0.3s;
  background-color: #f8f9fa; /* Light background for distinction */
}

.add-child-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  background-color: #e9ecef;
}

.add-child-card .card-image {
  position: relative;
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  
}

.add-child-card .add-icon-container {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-bottom: 2px solid #ddd;
}

.add-child-card .add-icon {
  font-size: 4rem; /* Large plus icon */
  color: var(--primary-color) /* Match primary color */
}

.add-child-card .card-content {
  padding: 1rem;
  text-align: center; 
}

.add-child-card .card-title {
  font-size: 1.2rem; /* Slightly larger for prominence */
  font-weight: 700;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.add-child-card .card-text {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0.5rem 0 0;
}
.modal-content{
  box-shadow: none !important;
  border: none !important;
}
.btn-close{
  z-index: 3;
  padding: 0 !important;
}

@media (max-width: 1200px) {
  .card-container > * {
    flex: 0 0 44%;
  }
}

@media (max-width: 768px) {
  .card-container > * {
    flex: 0 0 100%;
  }
  .header {
    flex-direction: column;
    align-items: flex-start;
  }
  .back-button {
    margin-bottom: 0.5rem;
  }
}
@media (max-width: 768px) {
  .modal-body{
    display: block !important;
  }
}
@media (max-width: 480px) {
  .modal-body{
    display: block !important;
  }
}
</style>